import React from 'react';
import { Card, CardContent } from '@/components/ui/Card';
import { UserMetricsSummary } from '@/types/domain/user';
import { Users, UserCheck, MailQuestion, ShieldAlert, UserX, Lock } from 'lucide-react';

interface UserKpiCardsProps {
  metrics: UserMetricsSummary;
  activeStatusFilter: string;
  onSelectStatusFilter: (status: string) => void;
}

export const UserKpiCards: React.FC<UserKpiCardsProps> = ({
  metrics,
  activeStatusFilter,
  onSelectStatusFilter,
}) => {
  const cards = [
    {
      id: 'ALL',
      title: 'Total Users',
      value: metrics.totalUsers,
      subtext: 'Master directory',
      icon: Users,
      color: 'text-primary bg-primary/10 border-primary/20',
      activeRing: 'border-primary ring-2 ring-primary/20',
    },
    {
      id: 'ACTIVE',
      title: 'Active',
      value: metrics.activeUsers,
      subtext: 'Operational access',
      icon: UserCheck,
      color: 'text-emerald-600 bg-emerald-500/10 border-emerald-500/20',
      activeRing: 'border-emerald-500 ring-2 ring-emerald-500/20',
    },
    {
      id: 'INVITATION_PENDING',
      title: 'Pending Invite',
      value: metrics.pendingInvitations,
      subtext: 'Awaiting onboarding',
      icon: MailQuestion,
      color: 'text-amber-600 bg-amber-500/10 border-amber-500/20',
      activeRing: 'border-amber-500 ring-2 ring-amber-500/20',
    },
    {
      id: 'BLOCKED',
      title: 'Quarantined',
      value: metrics.blockedUsers,
      subtext: 'Security block',
      icon: ShieldAlert,
      color: 'text-rose-600 bg-rose-500/10 border-rose-500/20',
      activeRing: 'border-rose-500 ring-2 ring-rose-500/20',
    },
    {
      id: 'INACTIVE',
      title: 'Deactivated',
      value: metrics.inactiveUsers,
      subtext: 'Offboarded accounts',
      icon: UserX,
      color: 'text-slate-600 bg-slate-500/10 border-slate-500/20',
      activeRing: 'border-slate-500 ring-2 ring-slate-500/20',
    },
    {
      id: 'LOCKED',
      title: 'Locked',
      value: metrics.lockedUsers,
      subtext: 'Failed auth attempts',
      icon: Lock,
      color: 'text-purple-600 bg-purple-500/10 border-purple-500/20',
      activeRing: 'border-purple-500 ring-2 ring-purple-500/20',
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3.5 font-poppins">
      {cards.map((c) => {
        const Icon = c.icon;
        const isSelected = activeStatusFilter === c.id;

        return (
          <Card
            key={c.id}
            onClick={() => onSelectStatusFilter(c.id)}
            className={`cursor-pointer transition-all duration-200 rounded-2xl shadow-2xs overflow-hidden ${
              isSelected ? `${c.activeRing} bg-card shadow-sm` : 'border-border/70 hover:border-primary/40 bg-card/70'
            }`}
          >
            <CardContent className="p-3.5 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-extrabold text-foreground-secondary uppercase tracking-wider">
                  {c.title}
                </span>
                <div className={`p-2 rounded-xl border ${c.color}`}>
                  <Icon className="h-4 w-4" />
                </div>
              </div>

              <div>
                <div className="text-2xl font-extrabold tracking-tight text-foreground font-mono">
                  {c.value}
                </div>
                <div className="text-[10px] text-foreground-muted mt-0.5 truncate">
                  {c.subtext}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
